import { motion } from "framer-motion";
import { Check, Sparkles, HeartHandshake, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Countdown } from "./Countdown";
import { ease } from "./Reveal";
import { CHECKOUT_URL, OFFER_INCLUDES, OFFER_BONUS } from "../../data/content";

const PriceLine = ({ label, price, testId }) => (
  <div data-testid={testId} className="flex flex-col gap-1 border-b border-white/10 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
    <span className="text-sm uppercase leading-snug tracking-[0.1em] text-[var(--cream)]/85">{label}</span>
    <span className="shrink-0 whitespace-nowrap text-sm text-[var(--cream)]/60">
      <span className="mr-1 text-[10px] uppercase tracking-widest">valor{price ? " original" : ":"}</span>
      {price ? <del>R$ {price}</del> : <span className="font-semibold tracking-[0.2em] text-[var(--gold)]">BÔNUS</span>}
    </span>
  </div>
);

const EXTRA_LINES = [
  "+80 aulas com mais de 1 hora de duração sobre Umbanda, espiritualidade ao todo e mediunidade",
  "Cursos profissionalizantes com certificado, +20 cursos e aulas na EspiritualFlix",
  "Aulas ao vivo, materiais, apostilas e mentoria",
];

const List = ({ items, icon: Icon, testId }) => (
  <ul data-testid={testId} className="space-y-3">
    {items.map((t) => (
      <li key={t} className="flex gap-3 text-sm leading-relaxed text-[var(--cream)]/90">
        <Icon size={16} className="mt-1 shrink-0 text-[var(--gold)]" strokeWidth={2} />
        {t}
      </li>
    ))}
  </ul>
);

export const OfferCard = () => (
  <motion.div
    id="vita"
    data-testid="black-friday-offer-card"
    initial={{ opacity: 0, y: 50, scale: 0.98 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 1.3, ease }}
    className="offer-glow relative mx-auto max-w-4xl scroll-mt-10 rounded-[32px] p-[1px]"
  >
    <div className="relative overflow-hidden rounded-[31px] bg-[var(--night)] px-5 py-10 sm:px-10 md:px-14 md:py-14">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#d9b45a]/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-20 h-72 w-72 rounded-full bg-[#7a8f4f]/20 blur-3xl" />
      <div className="grain absolute inset-0 opacity-60" />

      <div className="relative text-center">
        <span data-testid="offer-badge" className="inline-flex items-center gap-2 rounded-full border border-[#d9b45a]/40 bg-[#d9b45a]/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.32em] text-[var(--gold)]">
          <Sparkles size={13} /> Black Friday
        </span>
        <h3 className="font-display mt-6 text-4xl text-[var(--cream)] md:text-6xl">Plano Vitalício</h3>
        <p className="mx-auto mt-3 max-w-lg text-sm text-[var(--cream)]/75">
          Comunidade de Umbanda + EspiritualFlix,{" "}
          <strong data-testid="offer-forever-highlight" className="font-semibold text-[var(--gold)]">com acesso para sempre.</strong>
        </p>
        <p className="mt-8 text-[11px] uppercase tracking-[0.28em] text-[var(--cream)]/70">A oferta encerra em:</p>
        <div className="mt-3"><Countdown /></div>
      </div>

      <div className="relative mx-auto mt-10 max-w-xl">
        <PriceLine label="Comunidade de Umbanda" price="597,00" testId="offer-original-comunidade" />
        <PriceLine label="EspiritualFlix" price="997,00" testId="offer-original-espiritualflix" />
        {EXTRA_LINES.map((l, i) => (
          <PriceLine key={l} label={l} testId={`offer-bonus-line-${i}`} />
        ))}
      </div>

      <div className="relative mt-10 text-center">
        <p className="text-[11px] uppercase tracking-[0.32em] text-[var(--gold)]">Tudo vai sair por</p>
        <div data-testid="offer-installment-price" className="mt-3 flex items-end justify-center gap-2 text-[var(--cream)]">
          <span className="mb-3 text-xl md:mb-5 md:text-2xl">12x</span>
          <span className="font-display text-7xl leading-none md:text-[8.5rem]">R$&nbsp;46,44</span>
        </div>
        <p data-testid="offer-cash-price" className="mt-4 text-sm text-[var(--cream)]/70">
          ou <span className="text-[var(--cream)]">R$ 449,00</span> à vista
        </p>
        <p className="mx-auto mt-5 flex max-w-md items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] text-[var(--cream)]/80">
          <HeartHandshake size={16} className="shrink-0 text-[var(--gold)]" />
          Valor atual de Black Friday em apoio a projetos sociais caritativos
        </p>
      </div>

      <div className="relative mt-12 grid gap-6 md:grid-cols-2">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
          <h4 className="mb-5 text-[11px] font-semibold uppercase tracking-[0.32em] text-[var(--cream)]/70">O que está incluso</h4>
          <List items={OFFER_INCLUDES} icon={Check} testId="offer-includes-list" />
        </div>
        <div className="rounded-3xl border border-[#d9b45a]/25 bg-[#d9b45a]/[0.06] p-6">
          <h4 className="mb-5 text-[11px] font-semibold uppercase tracking-[0.32em] text-[var(--gold)]">Bônus</h4>
          <List items={OFFER_BONUS} icon={Sparkles} testId="offer-bonus-list" />
        </div>
      </div>

      <div className="relative mt-12 flex flex-col items-center gap-4">
        <a
          href={CHECKOUT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="offer-checkout-button"
          className="group inline-flex w-full max-w-md items-center justify-center gap-3 rounded-full bg-[var(--gold)] px-8 py-5 text-sm font-bold uppercase tracking-[0.18em] text-[var(--night)] transition-[transform,box-shadow] duration-500 hover:-translate-y-0.5 hover:shadow-[0_24px_60px_-20px_rgba(217,180,90,0.8)]"
        >
          Quero fazer parte
          <ArrowUpRight size={18} className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
        <p className="flex items-center gap-2 text-xs text-[var(--cream)]/60">
          <ShieldCheck size={14} /> Pagamento seguro via Hotmart
        </p>
      </div>
    </div>
  </motion.div>
);
